import { flash, getSymbol, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString, SelectField, TextareaField } from "common";
import { FormikValues } from "formik";
import { produce } from "immer";
import { filter, isNil, omit } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    AmountType,
    CatalogPromotionType,
    CatalogTermType,
    CollectionNode,
    GiftOffer,
    GiftOfferType,
    GreaterThanOrEqualToFilter,
    LineItemCode,
    LineItemTemplate,
    Permission,
    ProductObject,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleFilterObject,
    PurchaseRuleShortcutType,
} from "types";
import { number, object } from "yup";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useCollection } from "../../hooks/useCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useModal, useVisibility } from "../../hooks/useVisibility";
import { createAndFilter } from "../../models/RuleFilter";
import { PromptModal } from "../Common/PromptModal";
import { RadioInput } from "../Common/RadioInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { ContentLayout } from "../Layout/ContentLayout";
import { Loader } from "../Loader";
import { getProductCollectionPayload, ProductCollectionSelector } from "./ProductCollectionSelector";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

const validationSchema = object().shape({
    name: requiredString(),
    desc: requiredString(),
    price: number().when("type", {
        is: GiftOfferType.FIXED,
        then: number()
            .min(0)
            .required()
            .test("checkIfExceedsPrice", " should be higher or equal to purchase price", (value, form) => {
                if (!form.parent.purchasePrice) {
                    return true;
                }

                return value! >= form.parent.purchasePrice;
            }),
    }),
    purchasePrice: number().when("type", {
        is: GiftOfferType.FIXED,
        then: number().min(0, "must be greater or equal to 0").nullable(true).required(),
        otherwise: number().nullable(true),
    }),
    minimumPrice: number().when("type", {
        is: GiftOfferType.VARIABLE,
        then: number().min(0, "must be greater or equal to 0").nullable(),
        otherwise: number().nullable(true),
    }),
    discountAmount: number().when("type", {
        is: GiftOfferType.VARIABLE,
        then: number()
            .min(0, "must be greater or equal to 0")
            .required()
            .test("checkIfAmountExceedsPercentage", "must be less than or equal to 100", (value, form) => {
                if (form.parent.discountAmountType === AmountType.PERCENT && value && value > 100) {
                    return false;
                }

                return true;
            })
            .test("checkIfAmountExceedsMinimumPrice", "must be less than minimum price", (value, form) => {
                if (
                    form.parent.discountAmountType === AmountType.ABSOLUTE &&
                    value &&
                    (value > form.parent.minimumPrice || value === 0)
                ) {
                    return false;
                }

                return true;
            }),
        otherwise: number().nullable(true),
    }),
});

export const EditGiftOffer = ({ giftOffer, purchaseRule }: { giftOffer?: GiftOffer; purchaseRule?: PurchaseRule }) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const seller = useSeller();
    const collectionModal = useModal();
    const { data: discountTemplate, isLoading } = useLineItemTemplateByCode(LineItemCode.GIFT_OFFER_DISCOUNT);
    const [isConfirmationModalOpen, { show: showConfirmationModal, hide: hideConfirmationModal }] =
        useVisibility(false);

    const { isGranted } = useAuthorization();
    const showCollectionModification = isGranted(Permission.MODIFY_COLLECTIONS);

    const { data: addedCollections, isLoading: isLoadingAddedCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        giftOffer?.id ? { product: giftOffer.id } : undefined,
        { enabled: !!giftOffer?.id && showCollectionModification },
    );
    const addedCollectionPaths = addedCollections.map((node) => node.path ?? "").filter(Boolean);

    const { mutateAsync: saveCollections } = useMutation(
        async ({ newPaths, giftOfferId }: { newPaths: string[]; giftOfferId: string }) => {
            const collectionUpdateOperations = getProductCollectionPayload({
                newPaths,
                oldPaths: addedCollectionPaths,
                addedCollections,
                productId: giftOfferId,
            });
            if (collectionUpdateOperations.length > 0) {
                await api.patch("/collectionNodes", { ops: collectionUpdateOperations });
                invalidate("collectionNodes");
            }
        },
    );

    const { mutateAsync: submit } = useMutation(async (payload: Partial<GiftOffer>) => {
        try {
            if (giftOffer?.id) {
                await api.put(`/giftOffers/${giftOffer?.id}`, payload);
                await api.put(`/purchaseRules/${giftOffer.purchaseRule.id}`, payload.purchaseRule);
                return { data: giftOffer };
            } else {
                const { data: persistedGiftOffer } = await api.post<GiftOffer>("/giftOffers", payload, {
                    params: { expand: "purchaseRule" },
                });
                await api.put(`/purchaseRules/${persistedGiftOffer.purchaseRule.id}`, payload.purchaseRule);
                return { data: persistedGiftOffer };
            }
        } catch {
            flash.danger("Error saving Gift offer");
        }
    });

    if (isLoading) {
        return <Loader />;
    }

    const handleProductSubmit = async (values: FormikValues, applyToPastPurchases = false) => {
        const cascadeToClones = applyToPastPurchases ? { _cascadeToClones: ["filter", "actions"] } : null;

        if (discountTemplate) {
            const purchaseRulePayload = {
                ...getPurchaseRule(values, discountTemplate.id, purchaseRule),
                ...cascadeToClones,
            };

            const payload = produce(values, (draft) => {
                return omit(draft, [
                    "price",
                    "minimumPrice",
                    "purchasePrice",
                    "discount",
                    "discountAmountType",
                    "discountAmount",
                ]);
            });

            const response = await submit({ ...payload, purchaseRule: purchaseRulePayload });
            if (!response?.data) return;

            const giftOfferId = response.data.id;

            const hasCollectionChanges =
                showCollectionModification &&
                values.collections &&
                Array.isArray(values.collections) &&
                JSON.stringify([...(values.collections as string[])].sort()) !==
                    JSON.stringify([...addedCollectionPaths].sort());

            if (hasCollectionChanges) {
                await saveCollections({ newPaths: values.collections as string[], giftOfferId });
            }

            flash.show({ text: `Successfully ${giftOffer ? "updated" : "created"} Gift offer`, color: "success" });
            invalidate("products");
            invalidate("purchaseRules", giftOffer?.purchaseRule?.id);
            invalidate("giftOffers", giftOffer?.id);

            if (!giftOffer?.id) {
                history.push(`/products/giftOffers/${giftOfferId}/restrictions`);
            }
        }
    };

    const handleConfirmation = async (values: FormikValues) => {
        if (giftOffer?.id) {
            showConfirmationModal();
        } else {
            await handleProductSubmit(values, false);
        }
    };

    const handleCancel = () => {
        history.push(`/products/giftOffers`);
    };

    const initialValues = {
        name: "",
        desc: "",
        seller: { id: seller.id },
        currency: seller.currency,
        medias: [],
        object: ProductObject.GIFT_OFFER,
        emailReminder: false,
        emailReminderDays: null,
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                enablePrompt
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={{
                    ...initialValues,
                    ...giftOffer,
                    ...getTerms(giftOffer, purchaseRule),
                    collections: addedCollectionPaths,
                }}
                onSubmit={handleConfirmation}
            >
                {({ values, setFieldValue, isSubmitting, dirty, handleSubmit }: any) => {
                    return (
                        <>
                            <h2>Description</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section title="Description">
                                    <div className="flex-grow">
                                        <fieldset className="mb-16 flex-1 space-y-12">
                                            <div className="space-y-4">
                                                {showCollectionModification && (
                                                    <FormField name="collections" label="">
                                                        <ProductCollectionSelector
                                                            addedCollectionPaths={addedCollectionPaths}
                                                            isLoading={isLoadingAddedCollections}
                                                            value={values.collections ?? []}
                                                            isOpen={collectionModal.isVisible}
                                                            onChange={(collections: string[]) => {
                                                                setFieldValue("collections", collections);
                                                            }}
                                                            onOpenChange={(isOpen) =>
                                                                isOpen ? collectionModal.show() : collectionModal.hide()
                                                            }
                                                        />
                                                    </FormField>
                                                )}
                                                <FormField name="name" label="Name">
                                                    <InputField isRequired />
                                                </FormField>
                                                <FormField name="desc" label="Description">
                                                    <TextareaField rows={6} />
                                                </FormField>
                                            </div>
                                        </fieldset>
                                    </div>
                                </ContentLayout.Section>

                                <ContentLayout.Section title="Terms">
                                    <div className="flex-grow">
                                        <fieldset className="mb-16 flex-1">
                                            <div className="space-y-4">
                                                <RadioInput
                                                    checked={values.type === GiftOfferType.FIXED}
                                                    name="type"
                                                    value={GiftOfferType.FIXED}
                                                    onChange={(e) => setFieldValue("type", e.target.value)}
                                                >
                                                    Fixed Price
                                                </RadioInput>

                                                <RadioInput
                                                    checked={values.type === GiftOfferType.VARIABLE}
                                                    name="type"
                                                    value={GiftOfferType.VARIABLE}
                                                    onChange={(e) => setFieldValue("type", e.target.value)}
                                                >
                                                    Variable Price
                                                </RadioInput>

                                                {values.type === GiftOfferType.FIXED ? (
                                                    <div className="flex space-x-4">
                                                        <FormField name="purchasePrice" label="Purchase Price">
                                                            <CurrencyInputField isRequired isStyled />
                                                        </FormField>

                                                        <FormField name="price" label="Gift Value">
                                                            <CurrencyInputField isRequired isStyled />
                                                        </FormField>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="flex items-center space-x-1">
                                                            <FormField
                                                                name="discountAmount"
                                                                label="Discount"
                                                                className="flex-1"
                                                            >
                                                                {values.discountAmountType === AmountType.ABSOLUTE ? (
                                                                    <CurrencyInputField isRequired />
                                                                ) : (
                                                                    <InputField isRequired />
                                                                )}
                                                            </FormField>

                                                            <FormField name="discountAmountType" className="w-14">
                                                                <SelectField className="mt-5 h-10">
                                                                    <option
                                                                        className="no-translate"
                                                                        value={AmountType.ABSOLUTE}
                                                                    >
                                                                        {getSymbol(seller.currency)}
                                                                    </option>
                                                                    <option value={AmountType.PERCENT}>%</option>
                                                                </SelectField>
                                                            </FormField>
                                                        </div>

                                                        <FormField name="minimumPrice" label="Minimum Price">
                                                            <CurrencyInputField />
                                                        </FormField>
                                                    </>
                                                )}
                                            </div>
                                        </fieldset>
                                    </div>
                                </ContentLayout.Section>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton isLoading={isSubmitting} disabled={!dirty} onClick={handleSubmit}>
                                        Save
                                    </SubmitButton>

                                    <SecondaryButton type="reset" onClick={handleCancel}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>

                            <PromptModal
                                title="Apply new terms to past gift purchases?"
                                color="primary"
                                dialogText={{ submit: "Yes", secondary: "No" }}
                                isLoading={isSubmitting}
                                isOpen={isConfirmationModalOpen}
                                onConfirm={() => {
                                    handleProductSubmit(values, true);
                                    hideConfirmationModal();
                                }}
                                onClose={() => {
                                    handleProductSubmit(values, false);
                                    hideConfirmationModal();
                                }}
                            >
                                There could be existing purchases for the current terms of this gift offer. Do you want
                                to apply the new terms to existing gift offer purchases?
                            </PromptModal>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

export const useLineItemTemplateByCode = (code: string) => {
    const {
        data: [lineItemTemplate],
        isLoading,
    } = useCollection<LineItemTemplate>("lineItemTemplates", { code: code, limit: 1 });

    if (isLoading) {
        return { data: null, isLoading };
    }

    return { data: lineItemTemplate, isLoading };
};

const getPurchaseRule = (
    values: FormikValues,
    discountTemplateId: ID,
    currentPurchaseRule?: PurchaseRule,
): PurchaseRule => {
    const actions: PurchaseRuleAction[] = [];
    const filters: GreaterThanOrEqualToFilter[] = [];

    if (currentPurchaseRule && currentPurchaseRule.actions?.length > 0) {
        currentPurchaseRule.actions.map((action) => {
            if (
                action.object === PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION ||
                action.object === PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION
            ) {
                actions.push({
                    ...action,
                    _remove: true,
                });
            }

            return action;
        });
    }

    if (values.type === GiftOfferType.FIXED) {
        actions.push({
            object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
            filter: {
                object: PurchaseRuleFilterObject.EQUALS_FILTER,
                field: "template.code",
                operand: "gift",
            },
            shortcut: {
                field: "amount",
                type: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
                amount: values.price,
            },
        });

        // We should avoid creating action with $0 discount
        if (!isNil(values.purchasePrice) && values.purchasePrice !== values.price) {
            actions.push({
                object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
                template: {
                    id: discountTemplateId,
                },
                shortcut: {
                    field: "amount",
                    type: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
                    amount: -1 * (values.price - values.purchasePrice),
                },
            });
        }

        /**
         * For gift offer (variable price) GREATER_THAN_OR_EQUAL_TO_FILTER is used to specify the minimum price.
         * That's the only occurrence of that kind of filter in gift offers. Resetting to remove it when changing
         * the pricing to fixed.
         */

        if (currentPurchaseRule?.filter && "filters" in currentPurchaseRule.filter) {
            const hasVariablePriceFilter = currentPurchaseRule.filter?.filters?.some(
                (filter) => filter.object === PurchaseRuleFilterObject.GREATER_THAN_OR_EQUAL_TO_FILTER,
            );

            if (hasVariablePriceFilter) {
                return { actions, filter: null };
            }
        }

        return { actions };
    }

    filters.push({
        object: PurchaseRuleFilterObject.GREATER_THAN_OR_EQUAL_TO_FILTER,
        aggregator: {
            object: "return_subtotal_field_aggregator",
            subtotalCode: "gift",
            field: "amount",
        },
        operand: values.minimumPrice ?? 0,
    });

    const filter = createAndFilter({ filters });

    if (values.discountAmountType === AmountType.PERCENT) {
        actions.push({
            object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
            template: {
                id: discountTemplateId,
            },
            shortcut: {
                field: "amount",
                type: PurchaseRuleShortcutType.PERCENT,
                amount: -1 * values.discountAmount,
                options: {
                    templateCodes: ["gift"],
                },
            },
        });
    } else {
        actions.push({
            object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
            template: {
                id: discountTemplateId,
            },
            shortcut: {
                field: "amount",
                type: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
                amount: -1 * values.discountAmount,
            },
        });
    }

    return { actions, filter };
};

export interface FormTerms {
    discountAmount: number;
    discountAmountType: AmountType;
    price: number;
    purchasePrice?: number | null;
    minimumPrice?: number | null;
    type: GiftOfferType;
}

export const getTerms = (giftOffer?: GiftOffer, purchaseRule?: PurchaseRule) => {
    const terms: FormTerms = {
        discountAmount: 0,
        discountAmountType: AmountType.ABSOLUTE,
        price: giftOffer?.price ?? 0,
        purchasePrice: null,
        minimumPrice: null,
        type: giftOffer?.type ?? GiftOfferType.FIXED,
    };

    if (!purchaseRule?.actions) {
        if (giftOffer) {
            // Calculate minimumPrice from catalog promotion with type minimum_amount
            const minimumAmountCatalogTerms = filter(giftOffer.catalog?.terms, {
                type: CatalogTermType.MINIMUM_AMOUNT,
            });

            // Calculate discountAmount and discountAmountType from catalog promotion
            const discountCatalogPromotion = filter(giftOffer.catalog?.promotions, (promotion) =>
                [CatalogPromotionType.DISCOUNT_PERCENT, CatalogPromotionType.ABSOLUTE_DISCOUNT_PER_PURCHASE].includes(
                    promotion.type,
                ),
            );

            if (minimumAmountCatalogTerms?.length > 0) {
                terms.minimumPrice = minimumAmountCatalogTerms[0].value;
            }

            if (discountCatalogPromotion?.length > 0) {
                const isDiscountPercent = discountCatalogPromotion[0].type === CatalogPromotionType.DISCOUNT_PERCENT;
                terms.discountAmount = discountCatalogPromotion[0].value ?? 0;
                terms.discountAmountType = isDiscountPercent ? AmountType.PERCENT : AmountType.ABSOLUTE;

                if (giftOffer.type === GiftOfferType.FIXED) {
                    terms.price = discountCatalogPromotion[0].value ?? 0;
                }
            }
        }

        return { ...terms };
    }

    const giftOfferFilter = getGiftOfferTerm(purchaseRule);

    const insertLineAction = purchaseRule.actions.find(
        (action) => action.object === PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
    );
    const updateLineAction = purchaseRule.actions.find(
        (action) => action.object === PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
    );

    if (giftOfferFilter) {
        terms.type = GiftOfferType.VARIABLE;

        if (insertLineAction?.shortcut) {
            terms.discountAmount = -insertLineAction.shortcut.amount;
            terms.discountAmountType =
                insertLineAction.shortcut.type === PurchaseRuleShortcutType.PERCENT
                    ? AmountType.PERCENT
                    : terms.discountAmountType;
        }

        if (giftOfferFilter.operand) {
            terms.minimumPrice = giftOfferFilter.operand;
        }
    } else if (updateLineAction?.shortcut) {
        terms.price = updateLineAction.shortcut.amount ?? 0;

        terms.purchasePrice = insertLineAction?.shortcut
            ? updateLineAction.shortcut.amount + insertLineAction.shortcut.amount
            : terms.price;
    }

    return { ...terms };
};

export const getGiftOfferTerm = (purchaseRule: PurchaseRule): GreaterThanOrEqualToFilter | null => {
    if (purchaseRule.filter?.object !== "and_filter") {
        return null;
    }

    const filter = purchaseRule.filter?.filters.find((filter) => {
        return filter.object === "greater_than_or_equal_to_filter";
    }) as unknown as GreaterThanOrEqualToFilter;

    return filter ?? null;
};
